export { mountShadowRoot } from './mount';

import { createController } from './widget-controller';
import { readScriptConfig } from './bootstrap';

// One controller instance backs the entire `FeedbackTracker` global — this is the
// "singleton" the widget's public API operates on. Its methods are plain closures (not
// object-literal methods bound to `this`), so re-exporting them standalone below
// (`export const init = controller.init`) is safe to call as bare functions, exactly how
// `window.FeedbackTracker.init(...)` invokes them.
const controller = createController();

export const init = controller.init;
export const identify = controller.identify;
export const setContext = controller.setContext;
export const open = controller.open;
export const close = controller.close;
export const hide = controller.hide;
export const show = controller.show;
export const mountTrigger = controller.mountTrigger;
export const destroy = controller.destroy;

/**
 * Auto-init side effect: when this module is evaluated as the shipped IIFE `<script>` tag
 * embed, look for the widget's own `data-feedback-*` attributes and self-bootstrap without
 * the host app having to call `FeedbackTracker.init(...)` manually.
 *
 * Guarded so importing this module in a test/SSR/Node context — no `document`, no script
 * tag, or a `<script data-feedback-key>` simply not present — never auto-inits and never
 * throws. `readScriptConfig()` itself never throws, but this is intentionally defensive
 * too: nothing at module-evaluation time may ever propagate into the host page.
 */
try {
  if (typeof document !== 'undefined') {
    const scriptConfig = readScriptConfig();
    if (scriptConfig?.key) {
      init({
        key: scriptConfig.key,
        apiBase: scriptConfig.apiBase,
        redactSelectors: scriptConfig.redactSelectors,
        captureConsole: scriptConfig.captureConsole,
      });
    }
  }
} catch {
  // Swallow — auto-init must never break the host page just by being imported/loaded.
}
